"use client";
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  UserRound,
  ShoppingBag,
  ArrowUpLeft,
  Menu,
  ChevronDown,
  X,
  Plus,
  Minus,
  Trash2,
} from "lucide-react";
import { useCart } from "./cart-provider";
import { cartLineLimit } from "@/lib/cart";
import { money, number } from "@/lib/config";
import type { Category } from "@/lib/types";
import { ThemeToggle } from "./theme-toggle";
import { ProductImage } from "./product-image";
import { HeaderSearch } from "./header-search";
import cartStyles from "./cart-dropdown.module.css";
import { readSupabaseHeaderProfile } from "@/lib/supabase-auth-profile";

const defaultProfileAvatar = "/images/avatars/avatar-01.webp";
const profileAvatarPaths = new Set(
  Array.from({ length: 21 }, (_, index) => `/images/avatars/avatar-${String(index + 1).padStart(2, "0")}.webp`),
);

function isProfileAvatar(value: unknown): value is string {
  return typeof value === "string" && profileAvatarPaths.has(value);
}

export function Header({ categories, freeShippingText }: { categories: Category[]; freeShippingText: string }) {
  const { count, items, lines, ready, subtotal, update } = useCart();
  const path = usePathname();
  const [accountName, setAccountName] = useState("");
  const [accountAvatar, setAccountAvatar] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const scrolledRef = useRef(false);
  const [cartOpenPath, setCartOpenPath] = useState<string | null>(null);
  const cartOpen = cartOpenPath === path;
  const cartSavings = items.reduce((sum, item) => {
    const oldPrice = Number(item.product.oldPrice);
    return !item.product.demo && item.variant.price > 0 && Number.isFinite(oldPrice) && oldPrice > item.variant.price
      ? sum + (oldPrice - item.variant.price) * item.quantity
      : sum;
  }, 0);
  const closeCart = () => setCartOpenPath(null);
  const cartRef = useRef<HTMLDivElement>(null);
  const cartTrigger = useRef<HTMLButtonElement>(null);
  const cartHoverTimer = useRef<number | null>(null);
  const categoryMenu = useRef<HTMLDetailsElement>(null);
  const mobileMenu = useRef<HTMLDetailsElement>(null);
  const closeMenus = () => {
    if (categoryMenu.current) categoryMenu.current.open = false;
    if (mobileMenu.current) mobileMenu.current.open = false;
  };
  const cancelCartHoverClose = () => {
    if (cartHoverTimer.current !== null) window.clearTimeout(cartHoverTimer.current);
    cartHoverTimer.current = null;
  };
  const closeCartAfterHover = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") return;
    cancelCartHoverClose();
    cartHoverTimer.current = window.setTimeout(() => setCartOpenPath(null), 180);
  };
  useLayoutEffect(() => {
    let frame = 0;
    const syncForPath = () => {
      const threshold = window.innerWidth <= 800 ? 180 : 100;
      const next = window.scrollY > threshold;
      scrolledRef.current = next;
      setIsScrolled(next);
    };
    const update = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        // Keep a stable gap between entering and leaving the compact state. This
        // avoids flicker when mobile browsers report small scroll-bounce changes.
        const mobile = window.innerWidth <= 800;
        // The compact mobile header is much shorter than the full header.
        // Entering too early changes the document height enough to cross the
        // exit threshold again, making it flash on short scrolls.
        const enterAt = mobile ? 180 : 100;
        const exitAt = mobile ? 12 : 18;
        const next = scrolledRef.current ? y > exitAt : y > enterAt;
        if (next === scrolledRef.current) return;
        scrolledRef.current = next;
        setIsScrolled(next);
      });
    };
    syncForPath();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [path]);
  useEffect(() => {
    function closeOnOutside(event: PointerEvent) {
      if (
        categoryMenu.current &&
        !categoryMenu.current.contains(event.target as Node)
      ) {
        categoryMenu.current.open = false;
      }
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) setCartOpenPath(null);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (categoryMenu.current) categoryMenu.current.open = false;
      if (mobileMenu.current) mobileMenu.current.open = false;
      if (cartRef.current?.contains(document.activeElement)) cartTrigger.current?.focus();
      setCartOpenPath(null);
    }
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  useEffect(() => {
    let active = true;
    let profileRequest = 0;
    const readAuthProfile = () => {
      const user = readSupabaseHeaderProfile(document.cookie, process.env.NEXT_PUBLIC_SUPABASE_URL);
      setAccountName(user?.displayName.trim().slice(0, 28) || user?.email.split("@")[0] || "");
      setAccountAvatar(user ? (isProfileAvatar(user.avatarUrl) ? user.avatarUrl : defaultProfileAvatar) : "");
    };
    const readPreviewName = () => {
      const email = localStorage.getItem("nexo-account-preview-session") || "";
      const saved = email ? localStorage.getItem(`nexo-account-preview-profile:${email}`) || "" : "";
      const avatar = email ? localStorage.getItem(`nexo-account-preview-avatar:${email}`) : null;
      setAccountName(saved.trim().slice(0, 28) || (email ? email.split("@")[0] : ""));
      setAccountAvatar(email ? (isProfileAvatar(avatar) ? avatar : defaultProfileAvatar) : "");
    };
    const readNexoProfileCache = () => {
      try {
        const raw = localStorage.getItem("nexo3d_profile");
        if (!raw) return false;
        const profile = JSON.parse(raw) as { name?: unknown; avatar?: unknown };
        const name = typeof profile.name === "string" ? profile.name.replace(/[\u0000-\u001f<>]/g, "").trim().slice(0, 28) : "";
        const candidate = typeof profile.avatar === "string" && profile.avatar.startsWith("/images/")
          ? profile.avatar
          : typeof profile.avatar === "string" ? `/images/avatars/${profile.avatar}.webp` : "";
        if (!name && !isProfileAvatar(candidate)) return false;
        setAccountName(name || "حساب کاربری");
        setAccountAvatar(isProfileAvatar(candidate) ? candidate : defaultProfileAvatar);
        return true;
      } catch {
        return false;
      }
    };
    const readServerProfile = async () => {
      const request = ++profileRequest;
      try {
        // /account/* is routed to the customer-account app by Nginx, where the
        // signed customer session can be verified and the safe profile read.
        const response = await fetch("/account/api/profile", { cache: "no-store", credentials: "same-origin" });
        if (!response.ok || !active || request !== profileRequest) return;
        const result = await response.json() as { profile?: { name?: unknown; avatarUrl?: unknown } | null };
        const serverProfile = result.profile;
        if (!serverProfile || !active || request !== profileRequest) return;
        const name = typeof serverProfile.name === "string" ? serverProfile.name.trim().slice(0, 28) : "";
        const avatar = isProfileAvatar(serverProfile.avatarUrl) ? serverProfile.avatarUrl : defaultProfileAvatar;
        setAccountName(name || "حساب کاربری");
        setAccountAvatar(avatar);
      } catch {
        // The same-origin account profile endpoint is served by the account app;
        // the existing Supabase/local preview profile remains a safe fallback.
      }
    };
    const readAccount = () => {
      const hasNexoProfile = readNexoProfileCache();
      if (!hasNexoProfile) {
        readPreviewName();
        if (!localStorage.getItem("nexo-account-preview-session")) readAuthProfile();
        void readServerProfile();
      }
    };
    const showProfileUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: unknown; avatarUrl?: unknown }>).detail;
      if (!detail) return;
      setAccountName(typeof detail.name === "string" && detail.name.trim() ? detail.name.trim().slice(0, 28) : "حساب کاربری");
      setAccountAvatar(isProfileAvatar(detail.avatarUrl) ? detail.avatarUrl : defaultProfileAvatar);
    };
    const clearAccount = () => {
      setAccountName("");
      setAccountAvatar("");
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "nexo3d_profile" || event.key?.startsWith("nexo-account-preview-")) readAccount();
    };
    readAccount();
    window.addEventListener("focus", readAccount);
    document.addEventListener("visibilitychange", readAccount);
    window.addEventListener("nexo-auth-updated", readAccount);
    window.addEventListener("nexo-auth-signed-out", clearAccount);
    window.addEventListener("nexo-profile-updated", showProfileUpdate);
    window.addEventListener("nexo-preview-auth-change", readAccount);
    window.addEventListener("nexo3d-profile-change", readAccount);
    window.addEventListener("storage", onStorage);
    return () => {
      active = false;
      profileRequest += 1;
      window.removeEventListener("focus", readAccount);
      document.removeEventListener("visibilitychange", readAccount);
      window.removeEventListener("nexo-auth-updated", readAccount);
      window.removeEventListener("nexo-auth-signed-out", clearAccount);
      window.removeEventListener("nexo-profile-updated", showProfileUpdate);
      window.removeEventListener("nexo-preview-auth-change", readAccount);
      window.removeEventListener("nexo3d-profile-change", readAccount);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return (
    <>
    <div className={`header-shell${path === "/" ? " home-header-overlay" : ""}${isScrolled ? " is-scrolled" : ""}`}>
      <div className="announcement">
        <span>سفارش‌های بالای {freeShippingText}، ارسال رایگان</span>
      </div>
      <header className="site-header">
        <div className={`header-main wrap${cartOpen ? " cart-open" : ""}`}>
          <Link href="/" className="brand" aria-label="نکسو، صفحهٔ اصلی">
            <Image
              src={isScrolled ? "/images/brand/nexo-logo-123-scrolled.webp" : "/images/brand/nexo-logo-123-transparent.webp"}
              alt="نکسو سه‌بعدی"
              width={354}
              height={177}
              priority
              className="brand-logo"
            />
          </Link>
          <HeaderSearch compact />
          <div className="header-actions">
            <ThemeToggle />
            <Link href="/account" prefetch={false} aria-label={accountName ? `حساب ${accountName}` : "حساب کاربری"}>
              {accountAvatar ? (
                <span className="header-account-avatar" aria-hidden="true">
                  <Image
                    src={accountAvatar}
                    alt=""
                    width={30}
                    height={30}
                    sizes="30px"
                    unoptimized
                    loading="eager"
                    onError={() => setAccountAvatar((current) => current === defaultProfileAvatar ? "" : defaultProfileAvatar)}
                  />
                </span>
              ) : <UserRound size={21} aria-hidden="true" />}
              <span className="header-account-name">{accountName || "حساب کاربری"}</span>
            </Link>
            <span className="separator" />
            <div
              ref={cartRef}
              className={cartStyles.control}
              onPointerEnter={(event) => {
                cancelCartHoverClose();
                if (window.innerWidth > 800 && event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) setCartOpenPath(path);
              }}
              onPointerLeave={closeCartAfterHover}
            >
              <Link href="/cart" className={cartStyles.mobileCartLink} aria-label={`سبد خرید، ${number(count)} کالا`}>
                <ShoppingBag size={21} aria-hidden="true" />
                {count > 0 && <b className="cart-count" aria-live="polite">{number(count)}</b>}
              </Link>
              <button
                ref={cartTrigger}
                type="button"
                className={cartStyles.trigger}
                aria-label={`سبد خرید، ${number(count)} کالا`}
                aria-haspopup="dialog"
                aria-expanded={cartOpen}
                aria-controls="header-cart-preview"
                onClick={() => setCartOpenPath(path)}
              >
                <ShoppingBag size={21} aria-hidden="true" />
                <span>سبد خرید</span>
                {count > 0 && <b className="cart-count" aria-live="polite">{number(count)}</b>}
              </button>
              {cartOpen && (
                <section
                  id="header-cart-preview"
                  className={cartStyles.popover}
                  role="dialog"
                  aria-label="پیش‌نمایش سبد خرید"
                  onPointerEnter={cancelCartHoverClose}
                  onPointerLeave={closeCartAfterHover}
                >
                  <div className={cartStyles.heading}>
                    <div><strong>سبد خرید</strong><span>{ready ? `${number(count)} کالا` : "در حال بارگذاری…"}</span></div>
                    <button type="button" className={cartStyles.close} aria-label="بستن پیش‌نمایش سبد" onClick={closeCart}><X size={18} aria-hidden="true" /></button>
                  </div>
                  {!ready ? (
                    <p className={cartStyles.empty} role="status">در حال خواندن سبد خرید…</p>
                  ) : !items.length ? (
                    <div className={cartStyles.empty}>
                      <ShoppingBag size={30} aria-hidden="true" />
                      <strong>{lines.length ? "محصول‌های قبلی دیگر در کاتالوگ نیستند." : "سبد خرید خالی است."}</strong>
                      <span>برای دیدن محصولات، مجموعهٔ نکسو را مرور کنید.</span>
                    </div>
                  ) : (
                    <>
                      <div className={cartStyles.items} role="group" aria-label="محصول‌های سبد خرید">
                        {items.map((item) => {
                          const hasPrice = !item.product.demo && item.variant.price > 0;
                          return (
                            <article className={cartStyles.item} key={item.variantId}>
                              <Link href={`/products/${item.product.slug}`} className={cartStyles.image} tabIndex={cartOpen ? 0 : -1} onNavigate={closeCart}>
                                <ProductImage src={item.product.images[0]} alt={item.product.name} sizes="64px" />
                              </Link>
                              <div className={cartStyles.info}>
                                <Link href={`/products/${item.product.slug}`} className={cartStyles.name} tabIndex={cartOpen ? 0 : -1} onNavigate={closeCart}>{item.product.name}</Link>
                                <div className={cartStyles.quantity} role="group" aria-label={`تعداد ${item.product.name}`}>
                                  <button type="button" onClick={() => update(item.variantId, item.quantity - 1)} disabled={item.quantity <= 1} aria-label={`کاهش تعداد ${item.product.name}`}><Minus size={14} aria-hidden="true" /></button>
                                  <span aria-live="polite">{number(item.quantity)}</span>
                                  <button type="button" onClick={() => update(item.variantId, item.quantity + 1)} disabled={item.quantity >= cartLineLimit(item, item.variant)} aria-label={`افزایش تعداد ${item.product.name}`}><Plus size={14} aria-hidden="true" /></button>
                                </div>
                                {hasPrice && Number(item.product.oldPrice) > item.variant.price && (
                                  <del className={cartStyles.oldPrice}>{money(Number(item.product.oldPrice))}</del>
                                )}
                              </div>
                              <div className={cartStyles.linePrices}>
                                <strong className={cartStyles.lineTotal}>{hasPrice ? money(item.variant.price * item.quantity) : "—"}</strong>
                                <button
                                  type="button"
                                  className={cartStyles.removeItem}
                                  onClick={() => update(item.variantId, 0)}
                                  aria-label={`حذف ${item.product.name} از سبد خرید`}
                                  title="حذف از سبد خرید"
                                >
                                  <Trash2 size={14} aria-hidden="true" />
                                  حذف
                                </button>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                      <div className={cartStyles.subtotal}>
                        <span>جمع قیمت‌های ثبت‌شده</span>
                        <strong>{items.some((item) => item.product.demo || item.variant.price <= 0) ? "قیمت برخی محصولات ثبت نشده" : money(subtotal)}</strong>
                      </div>
                      {cartSavings > 0 && <div className={`${cartStyles.subtotal} ${cartStyles.savingsTotal}`}><span>سود شما از این خرید</span><strong>{money(cartSavings)}</strong></div>}
                    </>
                  )}
                  <Link href="/checkout" className={cartStyles.checkout} tabIndex={cartOpen ? 0 : -1} onNavigate={closeCart}>
                    تکمیل سفارش <ArrowUpLeft size={17} aria-hidden="true" />
                  </Link>
                  <Link href="/cart" className={cartStyles.viewCart} tabIndex={cartOpen ? 0 : -1} onNavigate={closeCart}>مشاهده و ویرایش سبد</Link>
                </section>
              )}
            </div>
          </div>
          <details ref={mobileMenu} className="wrap mobile-nav">
            <summary aria-label="منوی سایت">
              <Menu size={19} />
              <span>منو</span>
              <ChevronDown className="mobile-nav-chevron" size={17} />
            </summary>
            <nav className="mobile-nav-links" aria-label="منوی موبایل">
              <Link
                href="/products"
                onClick={closeMenus}
                aria-current={path === "/products" ? "page" : undefined}
              >
                محصولات
              </Link>
              <Link href="/#featured" onClick={closeMenus}>
                منتخب‌ها
              </Link>
              <Link href="/offers" onClick={closeMenus} aria-current={path === "/offers" ? "page" : undefined}>
                شگفت‌انگیزها
              </Link>
              <details className="mobile-category-dropdown">
                <summary>
                  دسته‌بندی‌ها <ChevronDown size={16} />
                </summary>
                <div className="mobile-category-dropdown-panel">
                  <Link href="/categories" onClick={closeMenus} aria-current={path === "/categories" ? "page" : undefined}>
                    همهٔ دسته‌بندی‌ها
                  </Link>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={`/categories/${category.slug}`}
                      onClick={closeMenus}
                      aria-current={path === `/categories/${category.slug}` ? "page" : undefined}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </details>
              <Link href="/#process" onClick={closeMenus}>
                داستان ساخت
              </Link>
              <Link href="/info/about" onClick={closeMenus} aria-current={path === "/info/about" ? "page" : undefined}>
                دربارهٔ ما
              </Link>
              <Link href="/info/faq" onClick={closeMenus} aria-current={path === "/info/faq" ? "page" : undefined}>
                پرسش‌ها
              </Link>
              <Link href="/support" prefetch={false} onClick={closeMenus} aria-current={path === "/support" ? "page" : undefined}>
                تماس با ما
              </Link>
            </nav>
          </details>
        </div>
        <div className="nav-border">
          <nav className="wrap nav-row" aria-label="منوی اصلی">
            <div className="nav-links">
              <Link
                href="/products"
                aria-current={path === "/products" ? "page" : undefined}
              >
                محصولات
              </Link>
              <details
                ref={categoryMenu}
                className="category-dropdown"
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) event.currentTarget.open = true;
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === "mouse" && !event.currentTarget.contains(document.activeElement)) event.currentTarget.open = false;
                }}
              >
                <summary
                  aria-current={
                    path.startsWith("/categories") ? "page" : undefined
                  }
                >
                  دسته‌بندی‌ها <ChevronDown size={16} />
                </summary>
                <div className="category-dropdown-panel">
                  <Link href="/categories" onClick={closeMenus} aria-current={path === "/categories" ? "page" : undefined}>
                    همهٔ دسته‌بندی‌ها
                  </Link>
                  <Link href="/products" onClick={closeMenus}>
                    همهٔ محصولات
                  </Link>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={`/categories/${category.slug}`}
                      onClick={closeMenus}
                      aria-current={
                        path === `/categories/${category.slug}`
                          ? "page"
                          : undefined
                      }
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </details>
              <Link href="/#featured" className="new-link">
                <i /> منتخب‌ها
              </Link>
              <Link
                href="/info/about"
                aria-current={path === "/info/about" ? "page" : undefined}
              >
                دربارهٔ ما
              </Link>
              <Link
                href="/info/faq"
                aria-current={path === "/info/faq" ? "page" : undefined}
              >
                پرسش‌ها
              </Link>
              <Link
                href="/support"
                prefetch={false}
                aria-current={path === "/support" ? "page" : undefined}
              >
                تماس با ما
              </Link>
            </div>
            <Link href="/#process" className="process-link">
              داستان ساخت <ArrowUpLeft size={16} />
            </Link>
          </nav>
        </div>
      </header>
    </div>
    </>
  );
}
